import React from 'react';
import { Button, Col, DatePicker, Drawer, Form, Input, Row, Select, Space, Switch, Tag, Tooltip, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { ACTIVITY_TYPES, ActivityType } from '../../services/ReminderService';
import { ACTIVITY_COLORS, ACTIVITY_LABELS } from '../../views/Reminders/constants';
import { normalizeTurkish } from '../../utils';
import {
  Agreement02Icon,
  CursorInWindowIcon,
  GoogleDocIcon,
  Mail02Icon,
  MapsLocation01Icon,
  MessageMultiple01Icon,
  SmartPhone01Icon,
  StarIcon,
  TaskDaily01Icon,
} from 'hugeicons-react';

const { TextArea } = Input;

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
const DOCUMENT_EXTS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx'];
const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
const ATTACHMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx';

const extractExtension = (name: string) => {
  const clean = (name || '').split('?')[0].toLowerCase();
  const dotIndex = clean.lastIndexOf('.');
  return dotIndex >= 0 ? clean.slice(dotIndex) : '';
};

const resolveFileName = (file: Partial<UploadFile> | File) => {
  if ('name' in file && file.name) return file.name;
  if ('url' in file && typeof file.url === 'string') {
    const parts = file.url.split('/');
    return parts[parts.length - 1] || '';
  }
  return '';
};

const isImageFile = (file: Partial<UploadFile> | File) => {
  const type = 'type' in file ? file.type || '' : '';
  if (type.startsWith('image/')) return true;
  const ext = extractExtension(resolveFileName(file));
  return IMAGE_EXTS.includes(ext);
};

const isDocumentFile = (file: Partial<UploadFile> | File) => {
  const type = 'type' in file ? file.type || '' : '';
  if (DOCUMENT_MIME_TYPES.some((t) => type === t || type.startsWith(t))) return true;
  const ext = extractExtension(resolveFileName(file));
  return DOCUMENT_EXTS.includes(ext);
};

const getFileKind = (file: Partial<UploadFile> | File) => {
  if (isImageFile(file)) return 'image';
  if (isDocumentFile(file)) return 'document';
  return null;
};

const validateAttachmentFile = (file: Partial<UploadFile> | File) => {
  const kind = getFileKind(file);
  if (!kind) {
    return null;
  }
  const size = 'size' in file ? file.size : undefined;
  if (size && size > MAX_UPLOAD_BYTES) {
    return null;
  }
  return kind;
};

const createUploadFile = (file: File): UploadFile => ({
  uid: `${Date.now()}-${file.name}-${Math.random().toString(16).slice(2)}`,
  name: file.name,
  size: file.size,
  type: file.type,
  originFileObj: file as any,
});

const REMINDER_CHANNEL_OPTIONS = [
  { value: 'PHONE', label: 'Telefon', icon: SmartPhone01Icon },
  { value: 'IN_PERSON', label: 'Yüz yüze', icon: MapsLocation01Icon },
  { value: 'ONLINE', label: 'Online', icon: CursorInWindowIcon },
  { value: 'EMAIL', label: 'E-posta', icon: Mail02Icon },
  { value: 'MESSAGE', label: 'Mesaj', icon: MessageMultiple01Icon },
  { value: 'SOFTWARE_PORTAL_FORM', label: 'Yazılım / Portal / Form', icon: TaskDaily01Icon },
  { value: 'DOCUMENT_RECORD', label: 'Doküman / Kayıt', icon: GoogleDocIcon },
  { value: 'OFFICIAL_LETTER', label: 'Resmi Yazı / Tebligat', icon: Agreement02Icon },
  { value: 'OTHER', label: 'Diğer', icon: StarIcon },
];

const renderChannelOptionLabel = (option: (typeof REMINDER_CHANNEL_OPTIONS)[number]) => {
  const Icon = option.icon;
  return (
    <Space size={8}>
      <Tooltip title={option.label}>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <Icon size={16} />
        </span>
      </Tooltip>
      <span>{option.label}</span>
    </Space>
  );
};

const selectFilter = (input: string, option?: any) => {
  const label = option?.label ?? option?.children;
  return normalizeTurkish(String(label || '')).includes(normalizeTurkish(input || ''));
};

interface CompletionDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  error?: string | null;
  result: string;
  onResultChange: (value: string) => void;
  switchActive: boolean;
  onSwitchChange: (checked: boolean) => void;
  activityType: ActivityType;
  onActivityTypeChange: (type: ActivityType) => void;
  channel?: string;
  onChannelChange: (value?: string) => void;
  reminderDate: Dayjs | null;
  onReminderDateChange: (value: Dayjs | null) => void;
  content: string;
  onContentChange: (value: string) => void;
  resultAttachments: UploadFile[];
  onResultAttachmentsChange: (files: UploadFile[]) => void;
  reminder?: {
    entityName?: string;
    statusId?: number;
    status?: { id?: number; name?: string } | string;
    proposalCreatedAt?: string;
    proposal?: { createdAt?: string; statusId?: number};
  } | null;
  proposal?: {
    createdAt?: string;
    statusId?: number;
    status?: { id?: number; name?: string };
    statusName?: string;
    statusTitle?: string;
  } | null;
}

const CompletionDrawer: React.FC<CompletionDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  error,
  result,
  onResultChange,
  switchActive,
  onSwitchChange,
  activityType,
  onActivityTypeChange,
  channel,
  onChannelChange,
  reminderDate,
  onReminderDateChange,
  content,
  onContentChange,
  reminder,
  proposal,
  resultAttachments,
  onResultAttachmentsChange,
}) => {
  const defaultTime = dayjs().hour(9).minute(0).second(0);
  const proposalCreatedAt =
    proposal?.createdAt ||
    reminder?.proposal?.createdAt ||
    reminder?.proposalCreatedAt;
  const statusId =
    reminder?.proposal?.statusId ??
    proposal?.statusId ??
    proposal?.status?.id;
  const isSentProposal = statusId === 3;
  const isManualFollow = statusId === 12;
  const proposalLimitDate = (() => {
    if (!proposalCreatedAt) return null;
    if (!isSentProposal && !isManualFollow) return null;
    const createdAtDate = dayjs(proposalCreatedAt);
    if (!createdAtDate.isValid()) return null;
    const limitDays = isSentProposal ? 30 : 60;
    return createdAtDate.add(limitDays, 'day').endOf('day');
  })();
  const proposalDescription = (() => {
    if (!proposalCreatedAt) return null;
    const isProposalRelated =
      (reminder?.entityName || '').toString().toUpperCase() === 'PROPOSAL' || !!proposalCreatedAt;
    if (!isProposalRelated) return null;
    const createdAtDate = dayjs(proposalCreatedAt);
    if (!createdAtDate.isValid()) return null;
    const createdAtLabel = createdAtDate.format('DD.MM.YYYY');
    const sentLimitLabel = createdAtDate.add(30, 'day').format('DD.MM.YYYY');
    const manualLimitLabel = createdAtDate.add(60, 'day').format('DD.MM.YYYY');
    return (
      <>
        <div><b>Oluşturulma Tarihi:</b> {createdAtLabel}</div>
        <div><b>Teklif Gönderildi:</b> {`${sentLimitLabel}`}</div>
        <div><b>Manuel Takip:</b> {`${manualLimitLabel}`}</div>
      </>
    );
  })();

  const handlePasteAttachments = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (submitting) return;
    const files = Array.from(event.clipboardData?.files ?? []);
    if (!files.length) return;
    const next: UploadFile[] = [];
    files.forEach((file) => {
      const kind = validateAttachmentFile(file);
      if (!kind) return;
      next.push(createUploadFile(file));
    });
    if (!next.length) return;
    event.preventDefault();
    onResultAttachmentsChange([...resultAttachments, ...next]);
  };

  const renderResultAttachmentFields = () => (
    <div onPaste={handlePasteAttachments}>
      <Upload.Dragger
        multiple
        accept={ATTACHMENT_ACCEPT}
        listType="picture"
        fileList={resultAttachments}
        beforeUpload={(file) => {
          const kind = validateAttachmentFile(file);
          if (!kind) return Upload.LIST_IGNORE;
          return false;
        }}
        onChange={({ fileList }) => onResultAttachmentsChange(fileList)}
        disabled={submitting}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <CursorInWindowIcon size={40} />
          <div style={{ fontSize: 12, color: '#1B1E27' }}>Sürükleyip bırakın, seçin veya Ctrl+V ile yapıştırın.</div>
          <div style={{ fontSize: 12, color: '#8C8C8C' }}>Görsel ve dokümanları tek alandan yükleyin. Maks 20MB</div>
        </div>
      </Upload.Dragger>
    </div>
  );

  return (
    <Drawer
      title="Görevi Tamamla"
      open={open}
      onClose={onClose}
      width={800}
      destroyOnClose
      footer={
        <div className="d-flex justify-content-end" style={{ gap: 8 }}>
          <Button onClick={onClose}>Vazgeç</Button>
          <Button type="primary" loading={submitting} onClick={onSubmit}>
            Tamamla
          </Button>
        </div>
      }
    >
      <div style={{ marginBottom: 8, fontSize: 12, color: proposalDescription ? '#6b7280' : undefined }}>
        {proposalDescription || 'Açıklama'}
      </div>
      <Form layout="vertical">
        <Row gutter={[0, 4]}>
          <Col span={24}>
            <Form.Item label="Sonuç" required style={{ marginBottom: 12 }}>
              <TextArea
                value={result}
                rows={3}
                placeholder="Görev sonucu giriniz."
                onChange={(e) => onResultChange(e.target.value)}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Sonuç Ekleri" style={{ marginBottom: 12 }}>
              {renderResultAttachmentFields()}
            </Form.Item>
          </Col>

          {error && (
            <Col span={24}>
              <div style={{ color: '#ff4d4f', fontSize: 12, marginBottom: 12 }}>{error}</div>
            </Col>
          )}

          <Col span={24}>
            <Form.Item label="Yeni görev oluştur" style={{ marginBottom: 12 }}>
              <Switch
                checked={switchActive}
                onChange={onSwitchChange}
              />
            </Form.Item>
          </Col>

          {switchActive && (
            <>
              <Col span={24}>
                <Form.Item label="İş Adımı" required style={{ marginBottom: 12 }}>
                  <Space wrap size={8}>
                    {ACTIVITY_TYPES.map((type) => (
                      <Tag.CheckableTag
                        key={type}
                        checked={activityType === type}
                        onChange={(checked) => checked && onActivityTypeChange(type)}
                        style={{
                          cursor: 'pointer',
                          borderColor: activityType === type ? ACTIVITY_COLORS[type] || '#1677ff' : undefined,
                          background: activityType === type ? '#f0f5ff' : undefined,
                          height: 36,
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 10px',
                        }}
                      >
                        <span style={{ color: '#444', fontWeight: activityType === type ? 600 : 500 }}>
                          {ACTIVITY_LABELS[type]}
                        </span>
                      </Tag.CheckableTag>
                    ))}
                  </Space>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label="İletişim Yöntemi" required style={{ marginBottom: 12 }}>
                  <Select
                    showSearch
                    allowClear
                    placeholder="İletişim yöntemi seçiniz"
                    optionFilterProp="label"
                    filterOption={selectFilter}
                    value={channel}
                    onChange={(value) => onChannelChange(value)}
                    style={{ width: '100%' }}
                  >
                    {REMINDER_CHANNEL_OPTIONS.map((option) => (
                      <Select.Option key={option.value} value={option.value} label={option.label}>
                        {renderChannelOptionLabel(option)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label="Görev Tarihi" required style={{ marginBottom: 12 }}>
                  <DatePicker
                    showTime={{ format: 'HH:mm', minuteStep: 5, defaultOpenValue: defaultTime }}
                    format="DD/MM/YYYY HH:mm"
                    needConfirm={true}
                    value={reminderDate}
                    onChange={(value) => onReminderDateChange(value)}
                    onOk={(value) => onReminderDateChange(value ?? null)}
                    disabledDate={(current) => !!proposalLimitDate && !!current && current.isAfter(proposalLimitDate)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label="İçerik" required style={{ marginBottom: 12 }}>
                  <TextArea
                    value={content}
                    rows={3}
                    placeholder="Yeni görev içeriğini düzenleyiniz."
                    onChange={(e) => onContentChange(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Drawer>
  );
};

export default CompletionDrawer;
